import { useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { getErrorMessage } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import { 
  Settings as SettingsIcon, Sun, Moon, Palette, Lock, Shield, 
  Mail, Trash2, Users, Loader2, AlertTriangle, Key
} from "lucide-react";

export default function Settings() {
  const { user, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  // States for settings
  const [sendingReset, setSendingReset] = useState(false);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [showTwoFASetup, setShowTwoFASetup] = useState(false);
  const [requestingEmailChange, setRequestingEmailChange] = useState(false);
  const [requestingDeletion, setRequestingDeletion] = useState(false);
  const [showDelegation, setShowDelegation] = useState(false);
  const [delegateEmail, setDelegateEmail] = useState("");
  const [delegateRole, setDelegateRole] = useState("ADVERTISER_USER");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const handleResetPassword = async () => {
    if (!user?.email) {
      toast.error("No email found for your account");
      return;
    }
    
    setSendingReset(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: user.email
      });
      
      if (response.data.email_sent) {
        toast.success("Password reset link sent to your email");
      } else {
        toast.info("If this email is registered, a reset link will be sent");
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error.response?.data?.detail, "Failed to send reset link");
      toast.error(errorMessage);
    } finally {
      setSendingReset(false);
    }
  };

  const handleToggle2FA = () => {
    if (!twoFAEnabled) {
      setShowTwoFASetup(true);
    } else {
      setTwoFAEnabled(false);
      toast.success("Two-factor authentication disabled");
    }
  };

  const handleSetup2FA = () => {
    // Mock 2FA setup
    setTwoFAEnabled(true);
    setShowTwoFASetup(false);
    toast.success("Two-factor authentication enabled");
  };

  const handleRequestEmailChange = () => {
    setRequestingEmailChange(true);
    setTimeout(() => {
      toast.success("Email change request submitted. Check your inbox for verification.");
      setRequestingEmailChange(false);
    }, 1500);
  };

  const handleRequestDeletion = () => {
    setRequestingDeletion(true);
    setTimeout(() => {
      toast.success("Account deletion request submitted. Our team will contact you within 48 hours.");
      setRequestingDeletion(false);
    }, 1500);
  };

  const handleAddDelegate = () => {
    if (!delegateEmail) {
      toast.error("Please enter an email address");
      return;
    }
    // Mock adding delegate
    toast.success(`Invitation sent to ${delegateEmail}`);
    setDelegateEmail("");
    setShowDelegation(false);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your account security and preferences
        </p>
      </div>

      {/* Password Reset */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-primary/10">
              <Key className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Reset Password</CardTitle>
              <CardDescription>Send a password reset link to your registered email</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">
                Current email: <span className="font-medium text-foreground">{user?.email}</span>
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={handleResetPassword}
              disabled={sendingReset}
            >
              {sendingReset ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Reset Link
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-primary/10">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Two-Factor Authentication</CardTitle>
              <CardDescription>Add an extra layer of security to your account</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm">Enable 2FA</span>
              {twoFAEnabled && (
                <Badge variant="outline" className="text-green-500 border-green-500/30 bg-green-500/10">
                  Active
                </Badge>
              )}
            </div>
            <Switch 
              checked={twoFAEnabled} 
              onCheckedChange={handleToggle2FA}
            />
          </div>

          {showTwoFASetup && (
            <div className="p-4 rounded-lg bg-muted/30 border border-border">
              <p className="text-sm font-medium mb-3">Set up Two-Factor Authentication</p>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-32 h-32 bg-muted rounded-lg flex items-center justify-center border">
                  <p className="text-xs text-muted-foreground text-center px-2">QR Code<br/>(Mock)</p>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground mb-2">
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                  <Label className="text-xs text-muted-foreground">Or enter this code manually:</Label>
                  <Input value="ABCD-EFGH-IJKL-MNOP" disabled className="mt-1 font-mono text-sm" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Enter verification code</Label>
                <div className="flex gap-2">
                  <Input placeholder="000000" maxLength={6} className="w-32" />
                  <Button onClick={handleSetup2FA}>Verify & Enable</Button>
                  <Button variant="outline" onClick={() => setShowTwoFASetup(false)}>Cancel</Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Email Change */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-primary/10">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Change Primary Email</CardTitle>
              <CardDescription>Request to change your account's primary email address</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">
                Email changes require verification of both old and new email addresses.
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={handleRequestEmailChange}
              disabled={requestingEmailChange}
            >
              {requestingEmailChange ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Request Change"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Delegation (Advertisers only) */}
      {!isAdmin && (
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-primary/10">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>Account Delegation</CardTitle>
                <CardDescription>Add additional users to manage your account</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              variant="outline" 
              onClick={() => setShowDelegation(!showDelegation)}
            >
              <Users className="mr-2 h-4 w-4" />
              Add User
            </Button>

            {showDelegation && (
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Email Address</Label>
                    <Input 
                      type="email"
                      value={delegateEmail}
                      onChange={(e) => setDelegateEmail(e.target.value)}
                      placeholder="colleague@example.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Role</Label>
                    <select 
                      value={delegateRole}
                      onChange={(e) => setDelegateRole(e.target.value)}
                      className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="owner">Owner - Full control, can transfer ownership</option>
                      <option value="administrator">Administrator - Full access except ownership</option>
                      <option value="finance">Finance - Billing, invoices, payment methods</option>
                      <option value="marketing">Marketing - Campaigns, creatives, analytics</option>
                      <option value="read_only_finance">Read Only (Finance) - View all sections</option>
                      <option value="read_only">Read Only - View all except billing</option>
                    </select>
                  </div>
                </div>
                
                {/* Role description */}
                <div className="mb-4 p-3 rounded-lg bg-secondary/50 text-xs text-muted-foreground">
                  {delegateRole === 'owner' && "Full control over the account including the ability to transfer ownership to another user."}
                  {delegateRole === 'administrator' && "Can manage all aspects of the account except transferring ownership."}
                  {delegateRole === 'finance' && "Access to billing, invoices, payment methods, and financial reports."}
                  {delegateRole === 'marketing' && "Can create/edit campaigns, manage creatives, view analytics, and configure targeting."}
                  {delegateRole === 'read_only_finance' && "View-only access to all sections including billing and financial data."}
                  {delegateRole === 'read_only' && "View-only access to all sections except billing and financial data."}
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowDelegation(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleAddDelegate}>Send Invitation</Button>
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Delegated users will receive an email invitation to join your account.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Appearance */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-primary/10">
              <Palette className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Choose your preferred theme</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => theme !== "light" && toggleTheme()}
              className={`p-4 rounded-xl border-2 transition-all ${
                theme === "light" 
                  ? "border-primary ring-2 ring-primary/20" 
                  : "border-border hover:border-muted-foreground/50"
              }`}
              data-testid="theme-light-btn"
            >
              <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <div className="h-2 w-16 bg-gray-800 rounded"></div>
                </div>
                <div className="space-y-1">
                  <div className="h-2 w-full bg-gray-200 rounded"></div>
                  <div className="h-2 w-3/4 bg-gray-200 rounded"></div>
                </div>
              </div>
              <p className="text-sm font-medium mt-3 flex items-center justify-center gap-2">
                <Sun className="h-4 w-4" /> Light
              </p>
            </button>
            
            <button
              onClick={() => theme !== "dark" && toggleTheme()}
              className={`p-4 rounded-xl border-2 transition-all ${
                theme === "dark" 
                  ? "border-primary ring-2 ring-primary/20" 
                  : "border-border hover:border-muted-foreground/50"
              }`}
              data-testid="theme-dark-btn"
            >
              <div className="bg-zinc-900 rounded-lg p-3 border border-zinc-700">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <div className="h-2 w-16 bg-zinc-100 rounded"></div>
                </div>
                <div className="space-y-1">
                  <div className="h-2 w-full bg-zinc-700 rounded"></div>
                  <div className="h-2 w-3/4 bg-zinc-700 rounded"></div>
                </div>
              </div>
              <p className="text-sm font-medium mt-3 flex items-center justify-center gap-2">
                <Moon className="h-4 w-4" /> Dark
              </p>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Request Account Deletion (Advertisers only) */}
      {!isAdmin && (
        <Card className="bg-card border-border border-destructive/30">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-destructive/10">
                <Trash2 className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <CardTitle className="text-destructive">Request Account Deletion</CardTitle>
                <CardDescription>Permanently delete your account and all associated data</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-destructive">This action cannot be undone</p>
                  <p className="text-muted-foreground mt-1">
                    Deleting your account will permanently remove all your campaigns, creatives, locations, and billing history. 
                    Any outstanding balances must be settled before deletion.
                  </p>
                </div>
              </div>
            </div>
            <Button 
              variant="destructive" 
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Request Account Deletion
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Confirm Account Deletion
            </DialogTitle>
            <DialogDescription className="pt-2">
              This action is permanent and cannot be undone. All your data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <p className="text-sm text-destructive font-medium">You will lose:</p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• All campaigns and their analytics</li>
                <li>• All creatives and company assets</li>
                <li>• All location data</li>
                <li>• Complete billing history</li>
              </ul>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Type <span className="font-mono font-bold">DELETE</span> to confirm:</Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="font-mono"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => {
              setShowDeleteConfirm(false);
              setDeleteConfirmText("");
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteConfirmText("");
                handleRequestDeletion();
              }}
              disabled={deleteConfirmText !== "DELETE" || requestingDeletion}
            >
              {requestingDeletion ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete My Account
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
